/** Mission Agent screen for one device: /missionagent/:deviceId.
 *
 *  The mission screen from the mission-agent dashboard (same panels, same
 *  brand theme), fed by the sentio read API (api/missionagent/...) and kept
 *  live by the device's Mission Agent socket. The header carries the mission,
 *  the device status, the latest observations and the mission summary's
 *  situation; beside it (60/40) a minimap of where the
 *  ambulance is and the road to its receiving facility; below, the handoff draft, the Mission
 *  Timeline (key moments), and open tasks with Recent Updates (everything,
 *  newest first). The patient care record is a PDF download in the top bar.
 *
 *  Read-only: drafts show with their sources; signing is not offered yet.
 */

import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from "@mui/material";
import { styled, ThemeProvider } from "@mui/material/styles";

import { type HandoverFormat, areaLayout, handoverFormat, setHandoverFormat } from "./api";
import { MissionCloseoutButton } from "./components/MissionCloseoutButton";
import { EndMissionButton } from "./components/EndMissionButton";
import { DraftPanel } from "./components/DraftPanel";
import { DebugInputsButton } from "./components/DebugInputsPanel";
import { SimulateMissionButton } from "./components/SimulateMissionPanel";
import { MissionHeader, MissionSummaryNote } from "./components/MissionHeader";
import { MissionMap } from "./components/MissionMap";
import { MissionPicker } from "./components/MissionPicker";
import { MissionTimeline } from "./components/MissionTimeline";
import { OpenTasksPanel } from "./components/OpenTasksPanel";
import { RecentUpdates } from "./components/RecentUpdates";
import { VitalsPanel } from "./components/VitalsPanel";
import { theme } from "./theme";
import { useMission } from "./useMission";
import { useMissionTrack } from "./useMissionTrack";

const Page = styled(Box)(({ theme }) => ({
  background: theme.palette.background.default,
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
  minHeight: "100%",
}));

const Shell = styled(Container)(({ theme }) => ({
  paddingTop: 0,
  paddingBottom: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const TopBar = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  paddingTop: theme.spacing(2),
  // Room for the one-line note hung under Generate ePCR.
  paddingBottom: theme.spacing(1.5),
}));

const Overline = styled(Typography)(({ theme }) => ({
  ...theme.typography.overline,
  color: theme.palette.text.secondary,
  display: "block",
}));

/** The mission header (60%) beside the ambulance's minimap (40%); stacked on narrow screens. */
const TopRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)",
  gap: theme.spacing(2),
  alignItems: "stretch",
  [theme.breakpoints.down("md")]: { gridTemplateColumns: "minmax(0, 1fr)" },
}));

/** Status and drafts | timeline and updates | tasks and the agent. */
const Columns = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr)",
  gap: theme.spacing(2),
  alignItems: "start",
  [theme.breakpoints.down("lg")]: { gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)" },
  [theme.breakpoints.down("md")]: { gridTemplateColumns: "minmax(0, 1fr)" },
}));

const Column = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  minWidth: 0,
}));

const Notice = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
}));

export default function MissionAgent() {
  const { deviceId } = useParams();
  const m = useMission(deviceId);
  const device = m.dashboard?.device;
  // Bumped when End mission starts the ePCR, so the ePCR button re-reads it.
  const [closeoutKey, setCloseoutKey] = useState(0);
  const track = useMissionTrack(m.dashboard?.mission_id ?? null, Boolean(m.dashboard?.ended_at));
  // The handoff's layout: the one for the receiving facility's area, unless this viewer picked another for
  // this mission and facility (a new mission or facility goes back to the area's). The dashboard sends the
  // handoff in every layout, so a switch is instant; a server that sends only the asked-for one is asked again.
  const auto = areaLayout(track.facility);
  const pickKey = `${m.dashboard?.mission_id ?? ""}|${track.facility?.id ?? ""}`;
  const [picked, setPicked] = useState<{ key: string; format: HandoverFormat } | null>(null);
  const handover = picked?.key === pickKey ? picked.format : auto.format;
  const applied = useRef(handoverFormat());
  useEffect(() => {
    if (applied.current === handover) return;
    applied.current = handover;
    setHandoverFormat(handover);
    const handoff = m.dashboard?.artifacts.find((a) => a.kind === "handoff_draft");
    if (handoff && !handoff.layouts?.[handover]) void m.refresh();
  }, [handover, m]);
  const changeHandover = (next: HandoverFormat) => setPicked({ key: pickKey, format: next });
  const layoutNote = auto.area
    ? `${auto.area[0].toUpperCase()}${auto.area.slice(1)}: the receiving facility's area uses this layout by default.`
    : "IMIST-AMBO by default; ISBAR for Trøndelag and Oslo, ATMIST(ER) for UK facilities.";

  return (
    <ThemeProvider theme={theme}>
      <Page>
        <Shell>
          <TopBar>
            <Box>
              <Overline>Mission Coordinator</Overline>
              <Typography variant="h1" component="h1">
                {device?.nickname || device?.device_type || `Device ${deviceId}`}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              {m.missions && m.missions.length > 0 ? (
                <MissionPicker
                  missions={m.missions}
                  missionId={m.missionId}
                  following={m.following}
                  onSelect={m.selectMission}
                />
              ) : null}
              <MissionCloseoutButton
                missionId={m.dashboard ? m.dashboard.mission_id : null}
                incident={m.selected?.intervention_id ?? m.dashboard?.mission_id ?? "mission"}
                refreshKey={closeoutKey}
              />
              {/* TEMPORARY: raw Sentio inputs, for development only: shown to Infiniwell staff and the device
                  organization's admins (the API decides). */}
              {deviceId ? (
                <DebugInputsButton deviceId={deviceId} interventionId={m.selected?.intervention_id ?? null} />
              ) : null}
              {/* Development only: shown where the API enables simulation. */}
              {deviceId ? <SimulateMissionButton deviceId={deviceId} onNewMission={m.followNewMission} /> : null}
              {/* Last, at the far right of the row. */}
              {deviceId ? (
                <EndMissionButton
                  deviceId={deviceId}
                  missionId={m.dashboard ? m.dashboard.mission_id : null}
                  incident={m.selected?.intervention_id ?? m.dashboard?.mission_id ?? "mission"}
                  endedAt={m.dashboard?.ended_at ?? null}
                  endedBy={m.dashboard?.ended_by ?? null}
                  suggestion={m.dashboard?.end_suggestion ?? null}
                  onEnded={() => {
                    setCloseoutKey((key) => key + 1);
                    // The engine reads the End Mission event within a moment or two.
                    void m.refresh();
                    setTimeout(() => void m.refresh(), 2500);
                  }}
                />
              ) : null}
            </Box>
          </TopBar>

          {m.error ? <Alert severity="error">{m.error}</Alert> : null}
          {m.missions === null || (m.loading && !m.dashboard) ? <CircularProgress size={24} /> : null}

          {m.missions !== null && m.missions.length === 0 ? (
            <>
              <Notice>
                No mission recorded for this device yet. One starts when the device reports an intervention;
                this page follows it automatically.
              </Notice>
            </>
          ) : null}

          {m.selected && !m.selected.in_engine ? (
            <>
              <Notice>
                Sentio recorded mission {m.selected.intervention_id}, but the Mission Coordinator engine never processed it
                (it was not running then), so there is nothing to show.
              </Notice>
            </>
          ) : null}

          {m.dashboard ? (
            <>
              <TopRow>
                <MissionHeader
                  dashboard={m.dashboard}
                  live={m.live}
                  liveDeviceStatus={m.following ? m.liveStatus.status : null}
                  liveStreaming={m.following ? m.liveStatus.streaming : null}
                  footer={<MissionSummaryNote dashboard={m.dashboard} onChanged={() => void m.refresh()} />}
                >
                  <VitalsPanel
                    embedded
                    latest={m.dashboard.latest_vitals}
                    observations={m.dashboard.observations}
                    trendReadIntervalS={m.dashboard.trend_read_interval_s}
                  />
                </MissionHeader>
                <MissionMap track={track} />
              </TopRow>
              <Columns>
                <Column>
                  <DraftPanel
                    title="RF Handoff"
                    artifact={m.dashboard.artifacts.find((a) => a.kind === "handoff_draft")}
                    emptyText="No handoff yet. It is drafted as the mission records facts."
                    progress={m.dashboard.drafting?.handoff_draft}
                    what="handoff"
                    zone={m.dashboard.time_zone}
                    layout={{ value: handover, onChange: changeHandover, note: layoutNote }}
                    pdf={{
                      missionId: m.dashboard.mission_id,
                      incident: m.selected?.intervention_id ?? m.dashboard.mission_id,
                    }}
                    signOff={{
                      missionId: m.dashboard.mission_id,
                      ended: Boolean(m.dashboard.ended_at),
                      drafting: Boolean(m.dashboard.drafting?.handoff_draft),
                      onSigned: () => void m.refresh(),
                    }}
                  />
                </Column>
                <Column>
                  <MissionTimeline dashboard={m.dashboard} />
                </Column>
                <Column>
                  <OpenTasksPanel
                    tasks={m.dashboard.open_tasks}
                    missingFields={m.dashboard.required_fields_missing}
                    handoffReadiness={m.dashboard.handoff_readiness}
                  />
                  <RecentUpdates items={m.updates} />
                </Column>
              </Columns>
            </>
          ) : null}
        </Shell>
      </Page>
    </ThemeProvider>
  );
}
